import { useRef, useState } from "react";
import type { QueryClient } from "@tanstack/react-query";
import {
  remixWithClips,
  saveAudioSelection,
  type AudioClipInstance,
  type TrackDefinition,
} from "@/api/client";
import {
  DEFAULT_AMBIENT_TRACK_ID,
  DEFAULT_SFX_TRACK_ID,
  MAX_CLIPS_PER_KIND,
  MAX_USER_TRACKS,
  SFX_CLIP_DUR,
  genTrackId,
} from "@/components/audio-editor/editorConstants";
import type { CommitClipsFn, AudioSelectionData } from "@/hooks/useClipHistory";


type Params = {
  sessionId: string | null | undefined;
  resolvedProjectName: string | undefined;
  clipsRef:   React.MutableRefObject<AudioClipInstance[]>;
  tracksRef:  React.MutableRefObject<TrackDefinition[]>;
  narrationVolumeRef: React.MutableRefObject<number>;
  ambientVolumeRef:   React.MutableRefObject<number>;
  sfxVolumeRef:       React.MutableRefObject<number>;
  storyDuration:      number;
  durByPathRender:    Record<string, number>;
  audioSelectionData: AudioSelectionData | undefined;
  setClips:           (clips: AudioClipInstance[]) => void;
  setTracks:          (tracks: TrackDefinition[]) => void;
  setAudioKey:        React.Dispatch<React.SetStateAction<number>>;
  setSelectedClipId:  (id: string | null) => void;
  queryClient:        QueryClient;
  /** Ref populated by the view after history hook is ready. Breaks the circular dep. */
  captureSnapshotRef: React.MutableRefObject<() => void>;
  triggerUpdateStatus: () => void;
};

const genClipId = () => `clip_${Math.random().toString(36).slice(2, 12)}`;

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * All clip/track CRUD operations plus persist + remix.
 * Returns `commitClips` which the view must wire into `commitClipsFnRef`
 * (used by useClipHistory) immediately after calling this hook.
 */
export function useClipMutations({
  sessionId, resolvedProjectName,
  clipsRef, tracksRef,
  narrationVolumeRef, ambientVolumeRef, sfxVolumeRef,
  storyDuration, durByPathRender, audioSelectionData,
  setClips, setTracks, setAudioKey, setSelectedClipId,
  queryClient, captureSnapshotRef, triggerUpdateStatus,
}: Params) {
  const [isRemixing, setIsRemixing] = useState(false);

  /** Stash the target lane id so addClip knows where a gallery pick lands. */
  const pendingAddTrackIdRef = useRef<string | null>(null);

  // ── Persistence ─────────────────────────────────────────────────────────────

  const deriveBackgrounds = (next: AudioClipInstance[]) => ({
    ambient:  next.find((c) => c.kind === "ambient")?.source ?? null,
    punctual: Array.from(new Set(next.filter((c) => c.kind === "sfx").map((c) => c.source))).slice(0, 5),
  });

  const persistClips = (
    next: AudioClipInstance[],
    vols?: { narration?: number; ambient?: number; sfx?: number },
    nextTracks?: TrackDefinition[],
  ) => {
    if (!sessionId || !resolvedProjectName) return;
    saveAudioSelection(sessionId, {
      project_name: resolvedProjectName,
      voices: (audioSelectionData?.voices ?? []) as string[],
      backgrounds: deriveBackgrounds(next),
      auto_backgrounds: audioSelectionData?.auto_backgrounds ?? false,
      tts_voice_id: audioSelectionData?.tts_voice_id ?? undefined,
      tts_provider: audioSelectionData?.tts_provider  ?? undefined,
      narration_volume: vols?.narration ?? narrationVolumeRef.current,
      ambient_volume:   vols?.ambient   ?? ambientVolumeRef.current,
      sfx_volume:       vols?.sfx       ?? sfxVolumeRef.current,
      clips:  next,
      tracks: nextTracks ?? tracksRef.current,
    }).catch((e) => console.warn("[AudioEditor] persistClips failed:", e));
  };

  const remixClips = async (
    next: AudioClipInstance[],
    vols?: { narration?: number; ambient?: number; sfx?: number },
  ) => {
    if (!sessionId) return;
    setIsRemixing(true);
    try {
      await remixWithClips(sessionId, next, {
        narrationVolume: vols?.narration ?? narrationVolumeRef.current,
        ambientVolume:   vols?.ambient   ?? ambientVolumeRef.current,
        sfxVolume:       vols?.sfx       ?? sfxVolumeRef.current,
      });
      setAudioKey((k) => k + 1);
      queryClient.invalidateQueries({ queryKey: ["scenario-audio", sessionId] });
    } finally {
      setIsRemixing(false);
    }
  };

  // ── Core commit (state + persist + optional remix) ───────────────────────────

  const commitClips: CommitClipsFn = (next, opts, nextTracks?: TrackDefinition[]) => {
    clipsRef.current = next;
    setClips(next);
    persistClips(next, opts?.vols, nextTracks);
    if (opts?.remix) void remixClips(next, opts.vols);
  };

  // ── Track helpers ────────────────────────────────────────────────────────────

  const pickTargetTrack = (kind: "ambient" | "sfx"): string => {
    const ofKind = tracksRef.current.filter((t) => t.kind === kind);
    for (const t of ofKind) {
      if (clipsRef.current.filter((c) => c.track_id === t.id).length < MAX_CLIPS_PER_KIND)
        return t.id;
    }
    return kind === "ambient" ? DEFAULT_AMBIENT_TRACK_ID : DEFAULT_SFX_TRACK_ID;
  };

  // ── Clip mutations ───────────────────────────────────────────────────────────

  const addClip = (source: string, kind: "ambient" | "sfx", trackId?: string) => {
    const candidate = trackId ?? pendingAddTrackIdRef.current;
    pendingAddTrackIdRef.current = null;
    const candidateValid =
      candidate != null &&
      tracksRef.current.some((t) => t.id === candidate && t.kind === kind);
    const targetTrackId = candidateValid ? candidate! : pickTargetTrack(kind);
    const sameTrack = clipsRef.current.filter((c) => c.track_id === targetTrackId);
    if (sameTrack.length >= MAX_CLIPS_PER_KIND) return;
    const srcDur  = durByPathRender[source];
    const lastEnd = sameTrack.reduce((m, c) => Math.max(m, c.start_seconds + c.duration_seconds), 0);
    const start   = kind === "ambient" ? 0 : lastEnd > 0 ? lastEnd + 1 : 0;
    const duration =
      kind === "ambient"
        ? srcDur && srcDur > 0 ? srcDur : storyDuration
        : srcDur && srcDur > 0 ? Math.min(srcDur, SFX_CLIP_DUR) : SFX_CLIP_DUR;
    captureSnapshotRef.current();
    const clip: AudioClipInstance = {
      id: genClipId(), source, kind, track_id: targetTrackId,
      start_seconds: start, source_offset_seconds: 0, duration_seconds: duration,
      volume: 1, fade_in_ms: 0, fade_out_ms: 0,
    };
    triggerUpdateStatus();
    commitClips([...clipsRef.current, clip], { remix: true });
  };

  const addTrack = (kind: "ambient" | "sfx") => {
    const sameKind = tracksRef.current.filter((t) => t.kind === kind);
    if (sameKind.length >= MAX_USER_TRACKS) return;
    const base = kind === "ambient" ? "Ambient" : "Effets";
    let n = sameKind.length + 1;
    const labels = new Set(sameKind.map((t) => t.label));
    while (labels.has(`${base} ${n}`)) n++;
    const newTrack: TrackDefinition = { id: genTrackId(), kind, label: `${base} ${n}`, volume: 1.0 };
    const next = [...tracksRef.current, newTrack];
    tracksRef.current = next;
    setTracks(next);
    persistClips(clipsRef.current, undefined, next);
  };

  const removeTrack = (trackId: string) => {
    if (trackId === DEFAULT_AMBIENT_TRACK_ID || trackId === DEFAULT_SFX_TRACK_ID) return;
    const track = tracksRef.current.find((t) => t.id === trackId);
    if (!track) return;
    const orphans = clipsRef.current.filter((c) => c.track_id === trackId);
    if (orphans.length > 0) {
      const defaultLabel = track.kind === "ambient" ? "Ambient 1" : "Effets 1";
      if (!window.confirm(`Cette piste contient ${orphans.length} son(s). Les déplacer vers "${defaultLabel}" ?`))
        return;
    }
    captureSnapshotRef.current();
    const fallback   = track.kind === "ambient" ? DEFAULT_AMBIENT_TRACK_ID : DEFAULT_SFX_TRACK_ID;
    const nextTracks = tracksRef.current.filter((t) => t.id !== trackId);
    tracksRef.current = nextTracks;
    setTracks(nextTracks);
    const nextClips = clipsRef.current.map((c) =>
      c.track_id === trackId ? { ...c, track_id: fallback } : c,
    );
    triggerUpdateStatus();
    commitClips(nextClips, { remix: orphans.length > 0 }, nextTracks);
  };

  const removeClip = (id: string) => {
    captureSnapshotRef.current();
    setSelectedClipId(null);
    triggerUpdateStatus();
    commitClips(clipsRef.current.filter((c) => c.id !== id), { remix: true });
  };

  const splitClipAt = (id: string, atTimelineSec: number): boolean => {
    const clip = clipsRef.current.find((c) => c.id === id);
    if (!clip) return false;
    const { start_seconds: start, duration_seconds: dur, source_offset_seconds: offset } = clip;
    const end = start + dur;
    if (atTimelineSec <= start + 0.2 || atTimelineSec >= end - 0.2) return false;
    const leftDur = atTimelineSec - start;
    const left:  AudioClipInstance = { ...clip, duration_seconds: leftDur };
    const right: AudioClipInstance = {
      ...clip,
      id: genClipId(),
      start_seconds:         atTimelineSec,
      source_offset_seconds: offset + leftDur,
      duration_seconds:      dur - leftDur,
    };
    captureSnapshotRef.current();
    setSelectedClipId(left.id);
    triggerUpdateStatus();
    commitClips(clipsRef.current.flatMap((c) => (c.id === id ? [left, right] : [c])), { remix: true });
    return true;
  };

  return {
    isRemixing,
    pendingAddTrackIdRef,
    commitClips,
    persistClips,
    remixClips,
    addClip,
    addTrack,
    removeTrack,
    removeClip,
    splitClipAt,
  };
}
